import { getPhoneDigits } from './normalizeRussianLocalPhoneNumber';

interface DeduplicatePastedPhoneParams {
  pastedText: string;
  countryTelCode: string;
  minDigits: number | null;
  maxDigits: number | null;
}

/**
 * Разбирает вставленный из буфера номер при уже выбранной стране (код страны
 * в поле уже стоит) и возвращает ПОЛНЫЙ номер в цифрах (с кодом страны),
 * которым надо заменить содержимое поля, — либо null, когда вставка не
 * похожа на целый номер и должна пройти как обычный ввод.
 *
 * Правила (us/10647):
 * - вставка начинается с «+<код выбранной страны>» — это явный международный
 *   формат, код из неё не дублируем независимо от длины;
 * - для России распознаём локальный формат 8XXXXXXXXXX как полный номер;
 * - вставка с кодом страны в начале и полной длиной номера страны
 *   (MinDigits..MaxDigits из словаря стран) — полный номер;
 * - вставка без кода с длиной «полный номер минус код» — дописываем код.
 */
export const deduplicatePastedPhone = ({
  pastedText,
  countryTelCode,
  minDigits,
  maxDigits,
}: DeduplicatePastedPhoneParams): string | null => {
  const pastedDigits = getPhoneDigits(pastedText);
  if (!pastedDigits || !countryTelCode) return null;

  if (pastedText.trim().startsWith(`+${countryTelCode}`)) {
    return pastedDigits;
  }

  if (countryTelCode === '7' && /^8\d{10}$/.test(pastedDigits)) {
    return `7${pastedDigits.slice(1)}`;
  }

  if (minDigits === null || maxDigits === null) return null;

  const isFullLength =
    pastedDigits.length >= minDigits && pastedDigits.length <= maxDigits;
  if (isFullLength && pastedDigits.startsWith(countryTelCode)) {
    return pastedDigits;
  }

  const bareMinLength = minDigits - countryTelCode.length;
  const bareMaxLength = maxDigits - countryTelCode.length;
  const isBareFullLength =
    pastedDigits.length >= bareMinLength &&
    pastedDigits.length <= bareMaxLength;
  if (isBareFullLength && !pastedDigits.startsWith(countryTelCode)) {
    return `${countryTelCode}${pastedDigits}`;
  }

  return null;
};
